<template>
    <Page>
        <Container class="max-w-screen-lg">
            <div class="mb-6 flex items-center justify-between gap-4">
                <Title class="!mb-0">Survey Responses</Title>
                <button
                    class="rounded bg-neutral-200 px-3 py-2 text-sm font-semibold dark:bg-neutral-800"
                    type="button"
                    @click="loadResponses"
                >
                    Refresh
                </button>
            </div>

            <div class="mb-4 flex flex-wrap gap-2">
                <button
                    v-for="filter in filters"
                    :key="filter.key"
                    class="rounded border px-3 py-2 text-sm font-semibold"
                    :class="activeFilter === filter.key
                        ? 'border-brand-primary-600 bg-brand-primary-600 text-white'
                        : 'border-neutral-300 text-neutral-700 dark:border-neutral-700 dark:text-neutral-200'"
                    type="button"
                    @click="activeFilter = filter.key"
                >
                    {{ filter.label }}
                    <span class="ml-1 text-xs opacity-75">{{ filterCount(filter) }}</span>
                </button>
            </div>

            <Loading v-if="loading" />

            <div v-else-if="visibleResponses.length === 0" class="rounded bg-neutral-100 p-4 text-sm text-neutral-600 dark:bg-neutral-800 dark:text-neutral-300">
                No survey responses have been submitted yet.
            </div>

            <div v-else class="space-y-4">
                <div
                    v-for="response in visibleResponses"
                    :key="response.id"
                    class="rounded bg-neutral-100 p-4 dark:bg-neutral-800"
                >
                    <div class="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                        <div class="min-w-0 flex-1">
                            <div class="mb-2 flex flex-wrap items-center gap-2">
                                <span
                                    class="rounded px-2 py-1 text-xs font-semibold"
                                    :class="response.rating >= 4
                                        ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100'
                                        : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-100'"
                                >
                                    {{ response.rating }} / 5
                                </span>
                                <span v-if="response.trustpilot_follow_up_sent_at" class="rounded bg-brand-primary-100 px-2 py-1 text-xs font-semibold text-brand-primary-800 dark:bg-brand-primary-900 dark:text-brand-primary-100">
                                    Trustpilot follow-up queued
                                </span>
                                <span class="text-xs text-neutral-500 dark:text-neutral-400">
                                    {{ formatDate(response.submitted_at || response.created_at) }}
                                </span>
                            </div>

                            <div v-if="response.feedback" class="text-sm leading-6 text-neutral-900 dark:text-white">
                                {{ response.feedback }}
                            </div>
                            <div v-else class="text-sm italic text-neutral-500 dark:text-neutral-400">
                                No written feedback.
                            </div>

                            <div class="mt-3 text-xs text-neutral-500 dark:text-neutral-400">
                                <span v-if="response.user">
                                    {{ response.user.first_name }} {{ response.user.last_name }} &lt;{{ response.user.email }}&gt;
                                </span>
                                <span v-else>Unknown user</span>
                                <span class="mx-2">/</span>
                                <span>{{ response.prompt_location || 'unknown prompt' }}</span>
                            </div>
                        </div>

                        <div class="w-full rounded bg-white p-3 text-xs leading-5 text-neutral-600 dark:bg-neutral-950 dark:text-neutral-300 md:w-64">
                            <div class="font-semibold text-neutral-900 dark:text-white">Attribution</div>
                            <div class="mt-2">User ID: {{ response.user_id || 'none' }}</div>
                            <div>PostHog: {{ response.posthog_distinct_id || 'not captured' }}</div>
                            <div>Source: {{ response.source || 'unknown' }}</div>
                        </div>
                    </div>
                </div>
            </div>
        </Container>
    </Page>
</template>

<script>
import {defineComponent} from 'vue';
import {mapActions} from 'vuex';
import moment from 'moment-timezone';

const filters = [
  {key: 'all', label: 'All', predicate: () => true},
  {key: 'needs_improvement', label: 'Needs improvement', predicate: response => response.rating <= 3},
  {key: 'happy', label: 'Happy', predicate: response => response.rating >= 4},
  {key: 'trustpilot', label: 'Trustpilot queued', predicate: response => Boolean(response.trustpilot_follow_up_sent_at)},
];

export default defineComponent({
  name: 'SurveyResponses',
  created() {
    this.loadResponses();
  },
  data() {
    return {
      activeFilter: 'all',
      filters,
      loading: false,
      responses: [],
    };
  },
  computed: {
    visibleResponses() {
      const filter = filters.find(item => item.key === this.activeFilter);

      return this.responses.filter(response => !filter || filter.predicate(response));
    },
  },
  methods: {
    ...mapActions({
      adminSurveyResponses: 'admin/surveyResponses',
    }),
    filterCount(filter) {
      return this.responses.filter(response => filter.predicate(response)).length;
    },
    formatDate(dateString) {
      if (!dateString) {
        return '';
      }

      return moment(dateString).fromNow();
    },
    async loadResponses() {
      this.loading = true;

      try {
        const response = await this.adminSurveyResponses();

        this.responses = response?.data?.data || [];
      } catch (error) {
        window.app.snackbarError(error);
      } finally {
        this.loading = false;
      }
    },
  },
});
</script>
